import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Modal, ModalProps, NumberInput, Stack, Title} from "@mantine/core";
import {useCallback} from "react";
import {
  ContainerClusterEntity,
  ContainerEntity,
  FeetInches,
  getObjectId,
  toTransportGaugeType,
  TransportCategory, transportGaugeType,
  TransportGaugeType
} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {TransportGaugeTypeSelect} from "../../select/entity/TransportGaugeTypeSelect/TransportGaugeTypeSelect";
import {ContainerSelect} from "../../select/entity/ContainerSelect/ContainerSelect";

interface TransportGaugeFormProps {
  category: TransportCategory
  containerCluster?: ContainerClusterEntity | string
  defaultValues?: Partial<TransportGaugeFormValues>;
  onSubmit?: (values: TransportGaugeFormValues) => void;
}

export interface TransportGaugeFormValues {
  container?: ContainerEntity | string;
  type: TransportGaugeType;
  topValue?: number
  bottomValue?: number
}

export interface TransportGaugeFormRawValues {
  container?: string
  type?: string
  topFeet?: number,
  topInches?: number,
  topQuarters?: number,
  bottomFeet?: number,
  bottomInches?: number,
  bottomQuarters?: number,
}

export const getInitialValues = (defaultValues: Partial<TransportGaugeFormValues> | undefined) : TransportGaugeFormRawValues => {
  let topFeet: number | undefined = undefined
  let topInches: number | undefined = undefined
  let topQuarters: number | undefined = undefined

  if (typeof defaultValues?.topValue !== "undefined") {
    const feetInches = FeetInches.fromInches(defaultValues.topValue)
    topFeet = feetInches.feet
    topInches = feetInches.inches
    topQuarters = feetInches.quarters
  }

  let bottomFeet: number | undefined = undefined
  let bottomInches: number | undefined = undefined
  let bottomQuarters: number | undefined = undefined

  if (typeof defaultValues?.bottomValue !== "undefined") {
    const feetInches = FeetInches.fromInches(defaultValues.bottomValue)
    bottomFeet = feetInches.feet
    bottomInches = feetInches.inches
    bottomQuarters = feetInches.quarters
  }

  return {
    container: getObjectId(defaultValues?.container),
    type: defaultValues?.type?.code ?? transportGaugeType.containerDefault.code,
    topFeet: topFeet,
    topInches: topInches,
    topQuarters: topQuarters,
    bottomFeet: bottomFeet,
    bottomInches: bottomInches,
    bottomQuarters: bottomQuarters,
  }
}

export const TransportGaugeForm = (props: TransportGaugeFormProps) => {
  const {
    category,
    containerCluster,
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: getInitialValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      let topValue: number | undefined = undefined
      let bottomValue: number | undefined = undefined

      const topFeet = values.topFeet
      const topInches = values.topInches
      const topQuarters = values.topQuarters
      if (typeof topFeet !== "undefined" || typeof topInches !== "undefined" || typeof topQuarters !== "undefined") {
        const feetInches = new FeetInches(topFeet, topInches, topQuarters)
        topValue = feetInches.asInches()
      }

      const bottomFeet = values.bottomFeet
      const bottomInches = values.bottomInches
      const bottomQuarters = values.bottomQuarters
      if (typeof bottomFeet !== "undefined" || typeof bottomInches !== "undefined" || typeof bottomQuarters !== "undefined") {
        const feetInches = new FeetInches(bottomFeet, bottomInches, bottomQuarters)
        bottomValue = feetInches.asInches()
      }


      onSubmit({
        container: getObjectId(values.container),
        type: toTransportGaugeType(values.type!),
        topValue: topValue,
        bottomValue: bottomValue,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <ContainerSelect
          label={t("common.entity.transportgauge.form.field.container", "Container")}
          placeholder={t<string>("common.entity.transportgauge.form.field.container", "Container")}
          selector={{
            regionId: regionId,
            containerClusterId: getObjectId(containerCluster) ?? "-1",
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("container")} />
        <TransportGaugeTypeSelect
          label={t("common.entity.transportgauge.form.field.type", "Type")}
          placeholder={t<string>("common.entity.transportgauge.form.field.type", "Type")}
          selector={{
            category: category,
          }}
          withinPortal={true}
          withAsterisk
          required
          clearable
          {...form.getInputProps("type")} />
        <Divider my="sm" variant="dotted"/>
        <Grid>
          <Grid.Col span={12}>
            <Title order={5}>
              {t("common.entity.transportgauge.form.field.top", "Top")}
            </Title>
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.feet", "Feet")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.feet", "Feet")}
              min={0}
              {...form.getInputProps("topFeet")} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.inches", "Inches")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.inches", "Inches")}
              min={0}
              max={11}
              {...form.getInputProps("topInches")} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.quarters", "Quarters")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.quarters", "Quarters")}
              min={0}
              max={3}
              {...form.getInputProps("topQuarters")} />
          </Grid.Col>
          <Grid.Col span={12}>
            <Title order={5}>
              {t("common.entity.transportgauge.form.field.bottom", "Bottom")}
            </Title>
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.feet", "Feet")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.feet", "Feet")}
              min={0}
              {...form.getInputProps("bottomFeet")} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.inches", "Inches")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.inches", "Inches")}
              min={0}
              max={11}
              {...form.getInputProps("bottomInches")} />
          </Grid.Col>
          <Grid.Col span={4}>
            <NumberInput
              label={t("common.entity.transportgauge.form.field.quarters", "Quarters")}
              placeholder={t<string>("common.entity.transport.form.field.quantity.quarters", "Quarters")}
              min={0}
              max={3}
              {...form.getInputProps("bottomQuarters")} />
          </Grid.Col>
        </Grid>
      </Stack>
      <Button type="submit" fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface TransportGaugeFormModalProps extends TransportGaugeFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const TransportGaugeFormModal = (props: TransportGaugeFormModalProps) => {
  const {
    category,
    containerCluster,
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <TransportGaugeForm
        category={category}
        containerCluster={containerCluster}
        defaultValues={defaultValues}
        onSubmit={onSubmit}/>
    </Modal>
  );
};
